import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { watchListApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import {
  EyeIcon,
  TrashIcon,
  BellIcon,
  BellSlashIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  TagIcon,
} from '@heroicons/react/24/outline';

interface WatchListItem {
  id: string;
  opportunityId: string;
  notes?: string;
  alertOnUpdate: boolean;
  addedAt: string;
  createdAt: string;
  updatedAt: string;
  opportunity: {
    id: string;
    samNoticeId: string;
    title: string;
    agency?: string;
    solicitationNumber?: string;
    naicsCode?: string;
    naicsCodes?: string[];
    setAside?: string;
    responseDeadline?: string;
    postedDate?: string;
    type?: string;
    uiLink?: string;
    description?: string;
    descriptionShort?: string;
  };
}

const WatchList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<WatchListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ total: 0, alertOnUpdate: 0 });
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchWatchList();
    fetchStats();
  }, []);

  const fetchWatchList = async () => {
    try {
      setLoading(true);
      const response = await watchListApi.getAll({ limit: 100 });
      setItems(response.data.data.items || []);
    } catch (err: any) {
      setError('Failed to load watch list');
      console.error('Error fetching watch list:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await watchListApi.getStats();
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleRemove = async (opportunityId: string) => {
    setActionLoading(opportunityId);
    try {
      await watchListApi.remove(opportunityId);
      setItems(items.filter(item => item.opportunityId !== opportunityId));
      fetchStats();
    } catch (err) {
      console.error('Error removing from watch list:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAlerts = async (item: WatchListItem) => {
    setActionLoading(item.opportunityId);
    try {
      await watchListApi.toggleAlerts(item.opportunityId, !item.alertOnUpdate);
      setItems(items.map(i => 
        i.opportunityId === item.opportunityId 
          ? { ...i, alertOnUpdate: !i.alertOnUpdate }
          : i
      ));
    } catch (err) {
      console.error('Error toggling alerts:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getDaysRemaining = (deadline?: string) => {
    if (!deadline) return null;
    const end = new Date(deadline);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Watch List</h1>
          <p className="text-gray-500 mt-1">
            Track opportunities and get notified when details are updated
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Watching</p>
          </div>
          <div className="h-10 w-px bg-gray-200"></div>
          <div className="text-right">
            <p className="text-2xl font-bold text-brand-600">{stats.alertOnUpdate}</p>
            <p className="text-sm text-gray-500">With Alerts</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {items.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
            <EyeIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Your Watch List is Empty</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Add opportunities to your watch list to track them and get notified when new details become available.
          </p>
          <Link
            to="/opportunities"
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
          >
            Browse Opportunities
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
      )}

      {/* Watch List Items */}
      {items.length > 0 && (
        <div className="space-y-4">
          {items.map((item) => {
            const opp = item.opportunity;
            const daysRemaining = getDaysRemaining(opp.responseDeadline);
            const hasDescription = opp.description && opp.description.length > 50;
            
            return (
              <div
                key={item.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Opportunity Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3">
                        <Link
                          to={`/opportunities/${opp.samNoticeId || opp.id}`}
                          className="flex-1"
                        >
                          <h3 className="text-lg font-semibold text-gray-900 hover:text-brand-600 line-clamp-2">
                            {opp.title || 'Untitled Opportunity'}
                          </h3>
                        </Link>
                        {!hasDescription && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 flex-shrink-0">
                            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                            Awaiting Details
                          </span>
                        )}
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
                        {opp.agency && (
                          <span className="flex items-center gap-1">
                            <BuildingOfficeIcon className="h-4 w-4" />
                            {opp.agency}
                          </span>
                        )}
                        {opp.solicitationNumber && (
                          <span className="flex items-center gap-1">
                            <TagIcon className="h-4 w-4" />
                            {opp.solicitationNumber}
                          </span>
                        )}
                        {opp.responseDeadline && (
                          <span className={`flex items-center gap-1 ${daysRemaining !== null && daysRemaining < 7 ? 'text-red-600 font-medium' : ''}`}>
                            <CalendarIcon className="h-4 w-4" />
                            Due {formatDate(opp.responseDeadline)}
                            {daysRemaining !== null && (
                              <span className="text-xs">
                                ({daysRemaining} days)
                              </span>
                            )}
                          </span>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {opp.naicsCode && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                            NAICS: {opp.naicsCode}
                          </span>
                        )}
                        {opp.setAside && opp.setAside !== 'None' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                            {opp.setAside}
                          </span>
                        )}
                        {opp.type && (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                            {opp.type}
                          </span>
                        )}
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium text-gray-500">
                          Added {formatDate(item.addedAt)}
                        </span>
                      </div>

                      {item.notes && (
                        <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                          {item.notes}
                        </p>
                      )}
                    </div>

                    {/* Right: Actions */}
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        {/* Alert Toggle */}
                        <button
                          onClick={() => handleToggleAlerts(item)}
                          disabled={actionLoading === item.opportunityId}
                          className={`p-2 rounded-lg border transition-colors ${
                            item.alertOnUpdate
                              ? 'bg-brand-50 text-brand-600 border-brand-200 hover:bg-brand-100'
                              : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100'
                          }`}
                          title={item.alertOnUpdate ? 'Disable alerts' : 'Enable alerts'}
                        >
                          {actionLoading === item.opportunityId ? (
                            <div className="animate-spin h-5 w-5 border border-gray-300 border-t-current rounded-full" />
                          ) : item.alertOnUpdate ? (
                            <BellIcon className="h-5 w-5" />
                          ) : (
                            <BellSlashIcon className="h-5 w-5" />
                          )}
                        </button>

                        {/* View on SAM.gov */}
                        <a
                          href={opp.uiLink || `https://sam.gov/opp/${opp.samNoticeId || opp.id}/view`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                          title="View on SAM.gov"
                        >
                          <LinkIcon className="h-5 w-5" />
                        </a>

                        {/* Remove */}
                        <button
                          onClick={() => handleRemove(item.opportunityId)}
                          disabled={actionLoading === item.opportunityId}
                          className="p-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50"
                          title="Remove from watch list"
                        >
                          {actionLoading === item.opportunityId ? (
                            <div className="animate-spin h-5 w-5 border border-red-300 border-t-red-500 rounded-full" />
                          ) : (
                            <TrashIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>

                      <Link
                        to={`/opportunities/${opp.samNoticeId || opp.id}`}
                        className="mt-2 text-sm text-brand-600 hover:text-brand-700 font-medium"
                      >
                        View Details →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WatchList;
